import type { RootState } from '../store';
import { ConsentAgreementKind, ConsentAgreementType } from 'constants/common';
import { getAcceptedCustomConsentIds } from 'utils/consentSteps';
import { hasWorkerAcceptedTheProject } from 'utils/onboarding';
import type { ConsentAgreement } from './state';

/**
 * Selects the consent agreement document matching `type` for the active language.
 * The cache holds a single flat list across all languages (one fetch). We match on
 * `kind === Standard` and `type`, prefer the active language (case-insensitive), and
 * fall back to the same agreement in any available language when the active-language
 * row is missing — so the step always renders and can record its acceptance, and so
 * this stays consistent with the (language-agnostic) route-guard completion check.
 * Returns `undefined` only while the cache is empty (fetch in flight) or the type is
 * absent entirely.
 */
export const selectConsentAgreement = (state: RootState, type: ConsentAgreementType): ConsentAgreement | undefined => {
  const activeLanguage = (state.app?.languageCode || 'en').toLowerCase();
  const agreements = (state.cache.consentAgreements || []).filter(
    agreement => agreement.kind === ConsentAgreementKind.Standard && agreement.type === type,
  );

  return agreements.find(agreement => (agreement.languageCode || '').toLowerCase() === activeLanguage) || agreements[0];
};

/**
 * Returns unsigned custom consent agreements for the given project, deduplicated
 * by consentAgreementId (same id = same form in different languages), preferred in
 * the active language, sorted by sequenceNumber ascending.
 * Returns an empty array when all are signed or none exist.
 *
 * "Unsigned" is resolved against the CURRENT worker's accepted-id set on the `onboarding` slice, not
 * against `cache.consentAgreements[].isAccepted` — that flag belongs to whichever worker was fetched
 * last, so reading it here made a second worker's custom forms silently disappear offline.
 *
 * Scope: custom forms are only ever OUTSTANDING while the worker still owes consent for this
 * onboarding. Once they have accepted the project they are done — a custom form an admin adds to the
 * project afterwards must not retroactively re-open their consent. The single exception is
 * `addEmployerFlow`, where consenting again to the project's current forms is the point of the flow.
 */
export const selectCustomConsentAgreements = (state: RootState, projectId?: string): ConsentAgreement[] => {
  const isAddEmployerFlow = !!state.onboarding?.temp?.addEmployerFlow;
  const hasAcceptedProject = hasWorkerAcceptedTheProject(state.onboarding?.assignedCompanies);
  if (!isAddEmployerFlow && hasAcceptedProject) {
    return [];
  }

  const activeLanguage = (state.app?.languageCode || 'en').toLowerCase();
  const acceptedContentIds = getAcceptedCustomConsentIds(state.onboarding);

  const customForms = (state.cache.consentAgreements || []).filter(
    a => a.kind === ConsentAgreementKind.Custom && (!projectId || a.projectId === projectId),
  );

  // Acceptance is recorded per CONTENT id, which identifies one language of one form. Signing is
  // language-agnostic though — accepting the English copy accepts the form — so promote the accepted
  // content ids to the FORM level (`consentAgreementId`) and hide every translation of that form.
  // Derived from the cache rather than stored, so there is no second accepted-set to keep in sync.
  const acceptedAgreementIds = new Set<string>(
    customForms.filter(a => acceptedContentIds.has(a.consentAgreementContentId)).map(a => a.consentAgreementId),
  );
  // Forms signed offline and still queued for upload are accepted too, as far as the UI is concerned.
  for (const id of state.onboarding?.temp?.customConsentAcceptances?.consentAgreementIds || []) {
    acceptedAgreementIds.add(id);
  }

  const all = customForms.filter(
    a => !acceptedContentIds.has(a.consentAgreementContentId) && !acceptedAgreementIds.has(a.consentAgreementId),
  );

  // The API returns one row per language per form (languageCode is 'en' | 'es'), so show only the
  // worker's active language — otherwise each form is presented once per translation.
  const inActiveLanguage = all.filter(a => (a.languageCode || '').toLowerCase() === activeLanguage);
  // Nothing in the active language → fall back to a single other language so the step is never
  // empty, rather than falling back to every language and reintroducing the duplicates.
  const fallbackLanguage = (all[0]?.languageCode || '').toLowerCase();
  const pool = inActiveLanguage.length
    ? inActiveLanguage
    : all.filter(a => (a.languageCode || '').toLowerCase() === fallbackLanguage);

  // Defensive only: within one language each form should already appear once.
  const byAgreementId = new Map<string, ConsentAgreement>();
  for (const a of pool) {
    if (!byAgreementId.has(a.consentAgreementId)) {
      byAgreementId.set(a.consentAgreementId, a);
    }
  }

  return Array.from(byAgreementId.values()).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
};
